/**
 * @license
 * Copyright 2025 Steven Roussey <sroussey@gmail.com>
 * SPDX-License-Identifier: Apache-2.0
 */

import { createServiceToken, makeFingerprint, uuid4 } from "@workglow/util";
import { DataPortSchemaObject, FromSchema, TypedArraySchemaOptions } from "@workglow/util/schema";
import { type ITabularMigration, type ITabularMigrationApplier } from "../migrations";
import {
  BaseTabularStorage,
  ClientProvidedKeysOption,
  KeyGenerationStrategy,
} from "./BaseTabularStorage";
import { pickCoveringIndex } from "./coveringIndexPicker";
import { InMemoryTabularMigrationApplier } from "./InMemoryTabularMigrationApplier";
import {
  AnyTabularStorage,
  AutoGeneratedKeys,
  CoveringIndexQueryOptions,
  DeleteSearchCriteria,
  InsertEntity,
  isSearchCondition,
  QueryOptions,
  SearchCriteria,
  SimplifyPrimaryKey,
  TabularChangePayload,
  TabularSubscribeOptions,
} from "./ITabularStorage";
import { StorageError } from "./StorageError";

export const MEMORY_TABULAR_REPOSITORY = createServiceToken<AnyTabularStorage>(
  "storage.tabularRepository.inMemory"
);

/** Non-persistent tabular storage backed by a `Map` keyed by row fingerprint. */
export class InMemoryTabularStorage<
  Schema extends DataPortSchemaObject,
  PrimaryKeyNames extends ReadonlyArray<keyof Schema["properties"]>,
  // computed types
  Entity = FromSchema<Schema, TypedArraySchemaOptions>,
  PrimaryKey = SimplifyPrimaryKey<Entity, PrimaryKeyNames>,
  Value = Omit<Entity, PrimaryKeyNames[number] & keyof Entity>,
  InsertType extends InsertEntity<Entity, AutoGeneratedKeys<Schema>> = InsertEntity<
    Entity,
    AutoGeneratedKeys<Schema>
  >,
> extends BaseTabularStorage<Schema, PrimaryKeyNames, Entity, PrimaryKey, Value, InsertType> {
  values = new Map<string, Entity>();
  private autoIncrementCounter = 0;
  /** Tracks whether the last put was an insert vs update — read by subscribeToChanges. */
  private _lastPutWasInsert = false;

  constructor(
    schema: Schema,
    primaryKeyNames: PrimaryKeyNames,
    indexes: readonly (keyof NoInfer<Entity> | readonly (keyof NoInfer<Entity>)[])[] = [],
    clientProvidedKeys: ClientProvidedKeysOption = "if-missing",
    tabularMigrations?: ReadonlyArray<ITabularMigration>,
    migrationName: string = "inmemory",
    uniqueIndexes: readonly (readonly (keyof NoInfer<Entity>)[])[] = []
  ) {
    super(
      schema,
      primaryKeyNames,
      indexes,
      clientProvidedKeys,
      tabularMigrations,
      migrationName,
      uniqueIndexes
    );
  }

  override async setupDatabase(): Promise<void> {
    if (this.tabularMigrations && this.tabularMigrations.length > 0) {
      await this.applyTabularMigrations();
    }
  }

  /** In-memory rows do not survive a process restart. */
  public isDurable(): boolean {
    return false;
  }

  public override getMigrationApplier(): ITabularMigrationApplier | null {
    return new InMemoryTabularMigrationApplier(this as unknown as AnyTabularStorage, "inmemory");
  }

  protected override generateKeyValue(
    columnName: string,
    strategy: KeyGenerationStrategy
  ): string | number {
    if (strategy === "autoincrement") {
      return ++this.autoIncrementCounter;
    } else {
      return uuid4();
    }
  }

  async put(value: InsertType): Promise<Entity> {
    let entityToStore = value as unknown as Entity;

    // Note: the autoincrement counter is intentionally never rolled back on a
    // failed put. Concurrent puts (e.g. via putBulk's Promise.all) interleave
    // around the `await` below, so restoring a snapshot could move the counter
    // backward past keys another in-flight put already claimed, producing
    // duplicate auto keys. A gap in the sequence is the correct, safe outcome.
    if (this.hasAutoGeneratedKey() && this.autoGeneratedKeyName) {
      const keyName = this.autoGeneratedKeyName as string;
      const clientProvidedValue = (value as Record<string, unknown>)[keyName];
      const hasClientValue = clientProvidedValue !== undefined && clientProvidedValue !== null;

      let shouldGenerate = false;
      if (this.clientProvidedKeys === "never") {
        shouldGenerate = true;
      } else if (this.clientProvidedKeys === "always") {
        if (!hasClientValue) {
          throw new Error(
            `Auto-generated key "${keyName}" is required when clientProvidedKeys is "always"`
          );
        }
        shouldGenerate = false;
      } else {
        shouldGenerate = !hasClientValue;
      }

      if (shouldGenerate) {
        const generatedValue = this.generateKeyValue(keyName, this.autoGeneratedKeyStrategy!);
        entityToStore = { ...value, [keyName]: generatedValue } as Entity;
      }
    }

    const { key } = this.separateKeyValueFromCombined(entityToStore);
    const id = await makeFingerprint(key);

    // Enforce DB-equivalent UNIQUE constraints at the in-memory tier so test
    // fixtures fail the same way Postgres/SQLite would. Skip the scan when no
    // uniqueIndexes are declared — the cost is proportional to row count, and
    // most stores never need it.
    if (this.uniqueIndexes.length > 0) {
      this.assertUniqueIndexes(entityToStore, id);
    }

    this._lastPutWasInsert = !this.values.has(id);
    this.values.set(id, entityToStore);

    this.events.emit("put", entityToStore);
    return entityToStore;
  }

  async putBulk(values: InsertType[]): Promise<Entity[]> {
    return await Promise.all(values.map(async (value) => this.put(value)));
  }

  /**
   * Scan the row Map and reject `entityToStore` if any *other* row (different
   * fingerprint id) already shares the same value tuple for any declared
   * unique-index tuple. Matches the DB-level `CREATE UNIQUE INDEX` semantics
   * the SQLite/Postgres backends emit so contract tests can exercise the
   * constraint without standing up a real DB.
   */
  private assertUniqueIndexes(entityToStore: Entity, incomingId: string): void {
    const incomingRecord = entityToStore as Record<string, unknown>;
    for (const tuple of this.uniqueIndexes) {
      const incomingValues = tuple.map((col) => incomingRecord[col as string]);
      // SQL UNIQUE treats NULL as distinct (one NULL never collides with
      // another) — match that semantics so a partially-populated tuple with a
      // null field never trips the constraint.
      if (incomingValues.some((v) => v === undefined || v === null)) continue;
      for (const [existingId, existing] of this.values) {
        if (existingId === incomingId) continue;
        const existingRecord = existing as Record<string, unknown>;
        const matches = tuple.every(
          (col, i) => existingRecord[col as string] === incomingValues[i]
        );
        if (matches) {
          throw new StorageError(
            `UNIQUE constraint failed: (${tuple.map(String).join(", ")}) ` +
              `= (${incomingValues.map(String).join(", ")})`
          );
        }
      }
    }
  }

  /**
   * Capture the mutable state required to roll back a batch op: the row
   * entries (a clone of the live `Map`) and the autoincrement counter. The
   * result is opaque to callers; pair with {@link restoreMutableState}.
   */
  protected snapshotMutableState(): {
    readonly entries: ReadonlyArray<readonly [string, Entity]>;
    readonly counter: number;
  } {
    return {
      entries: Array.from(this.values.entries()),
      counter: this.autoIncrementCounter,
    };
  }

  /**
   * Restore the row Map and autoincrement counter from a prior
   * {@link snapshotMutableState} result. Used by atomic batch overrides
   * (vector `putBulk`) to undo partial writes when a mid-batch error fires.
   */
  protected restoreMutableState(snapshot: {
    readonly entries: ReadonlyArray<readonly [string, Entity]>;
    readonly counter: number;
  }): void {
    this.values.clear();
    for (const [id, entity] of snapshot.entries) {
      this.values.set(id, entity);
    }
    this.autoIncrementCounter = snapshot.counter;
  }

  async get(key: PrimaryKey): Promise<Entity | undefined> {
    const id = await makeFingerprint(key);
    const out = this.values.get(id);
    this.events.emit("get", key, out);
    return out;
  }

  async delete(value: PrimaryKey | Entity): Promise<void> {
    const { key } = this.separateKeyValueFromCombined(value as Entity);
    const id = await makeFingerprint(key);
    this.values.delete(id);
    // A keyed delete emits only the key. Bulk deleteSearch carries the matched
    // row (it already has it in hand), and scoped callers delete via
    // deleteSearch so the owner columns reach subscribers without a read-back.
    this.events.emit("delete", key as Partial<Entity>);
  }

  async deleteAll(): Promise<void> {
    this.values.clear();
    this.events.emit("clearall");
  }

  async getAll(options?: QueryOptions<Entity>): Promise<Entity[] | undefined> {
    this.validateGetAllOptions(options);
    let all = Array.from(this.values.values());

    if (options?.orderBy && options.orderBy.length > 0) {
      all.sort((a, b) => {
        for (const { column, direction } of options.orderBy!) {
          const aVal = a[column] as string | number | null | undefined;
          const bVal = b[column] as string | number | null | undefined;
          if (aVal == null && bVal == null) continue;
          if (aVal == null) return direction === "ASC" ? -1 : 1;
          if (bVal == null) return direction === "ASC" ? 1 : -1;
          if (aVal < bVal) return direction === "ASC" ? -1 : 1;
          if (aVal > bVal) return direction === "ASC" ? 1 : -1;
        }
        return 0;
      });
    }

    if (options?.offset !== undefined) {
      all = all.slice(options.offset);
    }

    if (options?.limit !== undefined) {
      all = all.slice(0, options.limit);
    }

    return all.length > 0 ? all : undefined;
  }

  async size(): Promise<number> {
    return this.values.size;
  }

  async getOffsetPage(offset: number, limit: number): Promise<Entity[] | undefined> {
    const all = Array.from(this.values.values());

    // Sort by primary key for deterministic pagination order.
    all.sort((a, b) => {
      for (const key of this.primaryKeyNames) {
        const aVal = (a as Record<string, string | number>)[key as string];
        const bVal = (b as Record<string, string | number>)[key as string];
        if (aVal < bVal) return -1;
        if (aVal > bVal) return 1;
      }
      return 0;
    });

    const page = all.slice(offset, offset + limit);
    return page.length > 0 ? page : undefined;
  }

  async deleteSearch(criteria: DeleteSearchCriteria<Entity>): Promise<void> {
    const criteriaKeys = Object.keys(criteria) as Array<keyof Entity>;
    if (criteriaKeys.length === 0) {
      return;
    }

    // Materialize entries first so we don't iterate a Map while mutating it.
    const entries = Array.from(this.values.entries());

    const entriesToDelete = entries.filter(([_, entity]) => {
      for (const column of criteriaKeys) {
        const criterion = criteria[column];
        const columnValue = entity[column];

        if (isSearchCondition(criterion)) {
          const { value, operator } = criterion;
          const v = value as string | number;
          const cv = columnValue as string | number | null | undefined;
          switch (operator) {
            case "=":
              if (cv !== v) return false;
              break;
            case "<":
              if (cv === null || cv === undefined || !(cv < v)) return false;
              break;
            case "<=":
              if (cv === null || cv === undefined || !(cv <= v)) return false;
              break;
            case ">":
              if (cv === null || cv === undefined || !(cv > v)) return false;
              break;
            case ">=":
              if (cv === null || cv === undefined || !(cv >= v)) return false;
              break;
            default:
              return false;
          }
        } else {
          if (columnValue !== criterion) return false;
        }
      }
      return true;
    });

    for (const [id, entity] of entriesToDelete) {
      this.values.delete(id);
      // Emit the matched row as the deleted identity (it carries the owner
      // columns); InMemory already has it in hand, so this is not a read-back.
      this.events.emit("delete", entity);
    }
  }

  async query(
    criteria: SearchCriteria<Entity>,
    options?: QueryOptions<Entity>
  ): Promise<Entity[] | undefined> {
    this.validateQueryParams(criteria, options);

    const criteriaKeys = Object.keys(criteria) as Array<keyof Entity>;

    let results: Entity[] = Array.from(this.values.values()).filter((entity) => {
      for (const column of criteriaKeys) {
        const criterion = criteria[column];
        const columnValue = entity[column];

        if (isSearchCondition(criterion)) {
          const { value, operator } = criterion;
          const v = value as string | number;
          const cv = columnValue as string | number | null | undefined;
          switch (operator) {
            case "=":
              if (cv !== v) return false;
              break;
            case "<":
              if (cv === null || cv === undefined || !(cv < v)) return false;
              break;
            case "<=":
              if (cv === null || cv === undefined || !(cv <= v)) return false;
              break;
            case ">":
              if (cv === null || cv === undefined || !(cv > v)) return false;
              break;
            case ">=":
              if (cv === null || cv === undefined || !(cv >= v)) return false;
              break;
            default:
              return false;
          }
        } else {
          if (columnValue !== criterion) return false;
        }
      }
      return true;
    });

    if (options?.orderBy && options.orderBy.length > 0) {
      results.sort((a, b) => {
        for (const { column, direction } of options.orderBy!) {
          const aVal = a[column] as string | number | null | undefined;
          const bVal = b[column] as string | number | null | undefined;
          if (aVal == null && bVal == null) continue;
          if (aVal == null) return direction === "ASC" ? -1 : 1;
          if (bVal == null) return direction === "ASC" ? 1 : -1;
          if (aVal < bVal) return direction === "ASC" ? -1 : 1;
          if (aVal > bVal) return direction === "ASC" ? 1 : -1;
        }
        return 0;
      });
    }

    if (options?.offset !== undefined) {
      results = results.slice(options.offset);
    }

    if (options?.limit !== undefined) {
      results = results.slice(0, options.limit);
    }

    const result = results.length > 0 ? results : undefined;
    this.events.emit("query", criteria as Partial<Entity>, result);
    return result;
  }

  override async queryIndex<K extends keyof Entity & string>(
    criteria: SearchCriteria<Entity>,
    options: CoveringIndexQueryOptions<Entity, K>
  ): Promise<Pick<Entity, K>[]> {
    this.validateSelect(options);
    this.validateQueryParams(criteria, options);

    const registered = this.indexes.map((cols, i) => ({
      name: `idx_${i}`,
      keyPath: cols as string[],
    }));

    pickCoveringIndex({
      table: "InMemoryTabularStorage",
      indexes: registered,
      criteriaColumns: Object.keys(criteria),
      orderByColumns: (options.orderBy ?? []).map((o) => ({
        column: String(o.column),
        direction: o.direction,
      })),
      selectColumns: options.select.map(String),
      primaryKeyColumns: this.primaryKeyNames.map(String),
    });

    // pickCoveringIndex passed — reuse query() then project to the select set.
    const full = await this.query(criteria, {
      orderBy: options.orderBy,
      limit: options.limit,
      offset: options.offset,
    });
    if (!full) return [];
    return full.map((row) => {
      const out = {} as Pick<Entity, K>;
      for (const k of options.select) out[k] = row[k];
      return out;
    });
  }

  /** InMemory is both client and server, so changes flow through local events. */
  public override subscribeToChanges(
    callback: (change: TabularChangePayload<Entity>) => void,
    options?: TabularSubscribeOptions
  ): () => void {
    const handlePut = (entity: Entity) => {
      callback({ type: this._lastPutWasInsert ? "INSERT" : "UPDATE", new: entity });
    };

    const handleDelete = (key: Partial<Entity>) => {
      callback({ type: "DELETE", old: key as Entity });
    };

    const handleClearAll = () => {
      callback({ type: "DELETE" });
    };

    this.events.on("put", handlePut);
    this.events.on("delete", handleDelete);
    this.events.on("clearall", handleClearAll);

    return () => {
      this.events.off("put", handlePut);
      this.events.off("delete", handleDelete);
      this.events.off("clearall", handleClearAll);
    };
  }

  public override destroy(): void {
    this.values.clear();
  }
}
